type ScrollLockState = {
  scrollPosition: number;
  bodyStyles: {
    position: string;
    top: string;
    left: string;
    right: string;
    width: string;
  };
  htmlOverflow: string;
};

let scrollLockState: ScrollLockState | null = null;

/**
 * Disables page scrolling by fixing the body in place.
 * Uses position: fixed which is the most reliable cross-browser solution,
 * especially for iOS Safari which ignores overflow: hidden alone.
 * Preserves scroll position and existing styles to restore when re-enabling.
 */
export function disableScroll() {
  if (scrollLockState !== null) return;

  // Store current scroll position and existing styles before modifying
  scrollLockState = {
    scrollPosition: window.scrollY,
    bodyStyles: {
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
    },
    htmlOverflow: document.documentElement.style.overflow,
  };

  // Apply fixed positioning to body - this is the most reliable method
  // for preventing scroll on iOS Safari, Android, and desktop browsers
  document.body.style.position = 'fixed';
  document.body.style.top = `-${scrollLockState.scrollPosition}px`;
  document.body.style.left = '0';
  document.body.style.right = '0';
  document.body.style.width = '100%';

  // Also set overflow hidden on html element for additional safety
  document.documentElement.style.overflow = 'hidden';
}

/**
 * Re-enables page scrolling and restores the previous scroll position and styles.
 */
export function enableScroll() {
  if (!scrollLockState) return;

  const { scrollPosition, bodyStyles, htmlOverflow } = scrollLockState;

  // Restore original styles
  document.body.style.position = bodyStyles.position;
  document.body.style.top = bodyStyles.top;
  document.body.style.left = bodyStyles.left;
  document.body.style.right = bodyStyles.right;
  document.body.style.width = bodyStyles.width;
  document.documentElement.style.overflow = htmlOverflow;

  // Restore the scroll position after the browser has processed the style changes
  // This avoids a flash of content in some slow browsers
  requestAnimationFrame(() => {
    // Restore the scroll position
    window.scrollTo(0, scrollPosition);
  });

  // Clear the stored state
  scrollLockState = null;
}

export const scrollToTop = () => {
  window && window.scroll({ top: 0 });
};

export const restoreScrollPosition = (url: string) => {
  try {
    sessionStorage.setItem(`scrollPos:${url}`, JSON.stringify({ x: 0, y: 0 }));
  } catch {}
};

export const scrollElementIntoView = ({
  element,
  shouldFocus = true,
  scrollIntoViewOptions,
}: {
  element: HTMLElement;
  shouldFocus?: boolean;
  scrollIntoViewOptions?: ScrollIntoViewOptions;
}) => {
  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  );
  const behavior = prefersReducedMotion.matches ? 'auto' : 'smooth';
  element.scrollIntoView({ behavior, ...scrollIntoViewOptions });
  if (shouldFocus) {
    element.focus({ preventScroll: true });
  }
};
